import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import SelectController from '@/common/components/SelectController'
import { UnitTextField } from '@/common/components/UnitTextField'
import { options as unitForQuantity } from '@/common/constants/unitForQuantityOptions'
import { unitiesDecimals } from '@/common/constants/unities.constant'
import { formatCurrency } from '@/common/utils/formatters'
import { ContractProductFixedPrices } from '@/features/privateCompanies/components/updateContract/UpdateCreditLimitsModal/ContractProductFixedPrices'
import {
  UpdateContractCreditLimitsSchema,
  UpdateContractProductValidationSchema,
} from '@/features/privateCompanies/schemas/contract/updateCreditLimits.schema'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { options as priceRuleOptions } from '@/features/publicCompanyLeadsFunnel/constants/priceRuleOptions.constant'
import { FileBoxIcon, MapPinnedIcon, XIcon } from 'lucide-react'
import { useEffect } from 'react'
import { Controller, UseFieldArrayRemove, useFormContext } from 'react-hook-form'

interface ContractProductCategoryProductItemProps {
  contractProduct: UpdateContractProductValidationSchema
  productCategoryIndex: number
  productIndex: number
  removeContractProductCategoryProduct: UseFieldArrayRemove
  disabled: boolean
  contract?: CompanyContractType
}

export const ContractProductCategoryProductItem = ({
  contractProduct,
  productCategoryIndex,
  productIndex,
  removeContractProductCategoryProduct,
  disabled,
  contract,
}: ContractProductCategoryProductItemProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<UpdateContractCreditLimitsSchema>()

  const unitForQuantitySymbol = unitForQuantity.find(
    (unit) => unit.value === contractProduct?.product?.unit_for_quantity,
  )?.symbol

  const watchProductPriceRule = watch(
    `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.price_rule`,
  )
  const watchFilterByRegion = watch(
    `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.filterByRegion`,
  )

  const handleChangeFilterByRegion = ({
    valueIndex,
    productPriceId,
  }: {
    valueIndex: number
    productPriceId?: string
  }) => {
    setValue(
      `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.filterByRegion`,
      true,
    )

    if (productPriceId) {
      setValue(
        `contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.contract_product_category_product_prices.${valueIndex}.value`,
        formatCurrency(0),
      )
    }
  }

  const watchRequireProductCreditQuota = watch(`contract.require_product_credit_quota`)

  useEffect(() => {
    if (contractProduct.price_rule) {
      setValue(`contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.price_rule`, contractProduct.price_rule)
    }
  }, [])

  return (
    <Box className="flex flex-col gap-4">
      <div className="flex items-center">
        <div className="flex items-center gap-x-2 bg-white-200 rounded-l-sm px-2 py-1">
          <FileBoxIcon strokeWidth={1} className="size-6" />

          <p className="text-xs font-bold">{contractProduct?.product?.display_name}</p>
        </div>

        <Button
          onClick={() => removeContractProductCategoryProduct(productIndex)}
          className="size-8 bg-black hover:bg-black-900 rounded-none p-0"
          disabled={disabled}
        >
          <XIcon strokeWidth={1} />
        </Button>
      </div>

      {watchRequireProductCreditQuota && (
        <div className="flex gap-2">
          {contract?.consumption_controlled_by !== 'quantity' && (
            <Controller
              control={control}
              name={`contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.global_value_in_currency`}
              render={({ field: { onBlur, ...rest } }) => (
                <CurrencyTextField
                  dataTestid={`contract_product_category_${productCategoryIndex}_product_${productIndex}_global_value_in_currency_input`}
                  label="Cota total em valor"
                  markAsRequired
                  className="w-full"
                  {...rest}
                  errorMessage={
                    errors?.contract?.contract_product_categories_attributes?.[productCategoryIndex]
                      ?.contract_product_category_products?.[productIndex]?.global_value_in_currency?.message
                  }
                  disabled={disabled}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name={`contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.global_value_in_unit_for_consumption`}
            render={({ field: { onBlur, ...rest } }) => (
              <UnitTextField
                dataTestid={`contract_product_category_${productCategoryIndex}_product_${productIndex}_global_value_in_unit_for_consumption_input`}
                label="Cota total em quantidade"
                unit={unitForQuantitySymbol}
                markAsRequired
                className="w-full"
                decimals={contractProduct?.product?.unit_for_quantity ? unitiesDecimals[contractProduct?.product?.unit_for_quantity] : 2}
                {...rest}
                errorMessage={
                  errors?.contract?.contract_product_categories_attributes?.[productCategoryIndex]
                    ?.contract_product_category_products?.[productIndex]?.global_value_in_unit_for_consumption
                    ?.message
                }
                disabled={disabled}
              />
            )}
          />
        </div>
      )}

      <SelectController
        control={control}
        name={`contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.price_rule`}
        dataTestid={`contract_product_category_${productCategoryIndex}_product_${productIndex}_price_rule_selector`}
        label="Regras de preço"
        markAsRequired
        options={priceRuleOptions || []}
        isDisabled={disabled}
        errorMessage={
          errors?.contract?.contract_product_categories_attributes?.[productCategoryIndex]
            ?.contract_product_category_products?.[productIndex]?.price_rule?.message
        }
      />

      {watchProductPriceRule?.value === 'fixed' && (
        <>
          {watchFilterByRegion ? (
            <ContractProductFixedPrices
              productCategoryIndex={productCategoryIndex}
              productIndex={productIndex}
              disabled={disabled}
            />
          ) : (
            <>
              <Controller
                control={control}
                name={`contract.contract_product_categories_attributes.${productCategoryIndex}.contract_product_category_products.${productIndex}.contract_product_category_product_prices.0.value`}
                render={({ field: { onBlur, ...rest } }) => (
                  <CurrencyTextField
                    dataTestid={`contract-product-${productIndex}-price_rule_value-0`}
                    label="Preço fixo a ser praticado"
                    {...rest}
                    errorMessage={
                      errors?.contract?.contract_product_categories_attributes?.[productCategoryIndex]
                        ?.contract_product_category_products?.[productIndex]
                        ?.contract_product_category_product_prices?.[0]?.value?.message
                    }
                    disabled={disabled}
                    markAsRequired
                  />
                )}
              />

              <Button
                type="button"
                variant="link"
                className="gap-2 ml-auto hover:no-underline"
                name={`contract_product_price_rule_by_region_${productIndex}`}
                onClick={() =>
                  handleChangeFilterByRegion({
                    valueIndex: 0,
                    productPriceId: contractProduct.contract_product_category_product_prices?.[0]?.id || '',
                  })
                }
                disabled={disabled}
              >
                <MapPinnedIcon strokeWidth={1} className="size-4" />
                <p className="text-[11px]">Filtrar por região</p>
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  )
}
